import { useCallback, useRef, useState } from 'react';

// Must stay in sync with HONEYPOT_FIELD in frontend/api/_lib/antispam.js
export const HONEYPOT_FIELD = 'company_website';

const SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

let scriptPromise = null;

/**
 * Inject the reCAPTCHA v3 script once, on demand.
 *
 * Loading it lazily (on first form interaction rather than on every page view)
 * keeps the landing page fast and avoids handing Google a pageview from
 * visitors who never touch a form.
 */
export function loadRecaptcha() {
    if (!SITE_KEY) return Promise.resolve(null);
    if (scriptPromise) return scriptPromise;

    scriptPromise = new Promise((resolve, reject) => {
        if (window.grecaptcha) {
            resolve(window.grecaptcha);
            return;
        }

        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${SITE_KEY}`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve(window.grecaptcha);
        script.onerror = () => {
            scriptPromise = null;
            reject(new Error('Failed to load reCAPTCHA'));
        };
        document.head.appendChild(script);
    });

    return scriptPromise;
}

/**
 * Get a reCAPTCHA v3 token for a given action.
 * Resolves to null if reCAPTCHA is unavailable — the server treats a missing
 * token as a failure only when it has a secret key configured.
 */
export async function getRecaptchaToken(action) {
    if (!SITE_KEY) return null;

    try {
        const grecaptcha = await loadRecaptcha();
        if (!grecaptcha) return null;

        return await new Promise((resolve) => {
            grecaptcha.ready(() => {
                grecaptcha
                    .execute(SITE_KEY, { action })
                    .then(resolve)
                    .catch(() => resolve(null));
            });
        });
    } catch (error) {
        console.warn('reCAPTCHA unavailable:', error.message);
        return null;
    }
}

/**
 * Bot-protection plumbing shared by every public form.
 *
 * @param {string} action - reCAPTCHA action name, e.g. 'contact_form'
 * @returns {{
 *   honeypotProps: {value: string, onChange: Function},
 *   onInteraction: Function,
 *   getProtectionPayload: () => Promise<Object>
 * }}
 */
export function useFormProtection(action) {
    const mountedAt = useRef(Date.now());
    const [honeypot, setHoneypot] = useState('');

    // Warm the script up as soon as the user touches the form so the token is
    // ready by the time they hit submit.
    const onInteraction = useCallback(() => {
        loadRecaptcha().catch(() => {});
    }, []);

    const getProtectionPayload = useCallback(async () => {
        const token = await getRecaptchaToken(action);
        return {
            [HONEYPOT_FIELD]: honeypot,
            form_elapsed_ms: Date.now() - mountedAt.current,
            recaptcha_token: token
        };
    }, [action, honeypot]);

    return {
        honeypotProps: {
            value: honeypot,
            onChange: (e) => setHoneypot(e.target.value)
        },
        onInteraction,
        getProtectionPayload
    };
}
